/**
 * Client-side half of the prerendered snapshot's "probably signed in" hint.
 *
 * Site-arche snapshots are static objects cached at the edge and served to
 * everyone, signed-in or not (the CloudFront rewrite for `/` and `/a/*` is
 * unconditional by design — see CLAUDE.md § Site Arches). So a signed-in user
 * landing on `/` is served the marketing HTML and would read it until the app
 * boots, resolves `/auth/me` and redirects.
 *
 * `SESSION_HINT_SCRIPT` in `@fias/site-snapshot` covers that window: it runs in
 * the snapshot's `<head>`, and when this browser has held a session it stamps
 * `data-fias-session-hint="1"` on the root element, which hides the marketing
 * markup and shows a loading indicator instead.
 *
 * That attribute is a PRE-JS guess and nothing else in the document clears it,
 * so the app owns the teardown. `clearBootSessionHint` must run once auth has
 * settled — BOTH ways it can settle:
 *
 *   - signed in  → the app has redirected, the splash has done its job;
 *   - signed out → the hint was stale (a logged-out browser that still had the
 *     localStorage key, e.g. after a session was revoked server-side), and the
 *     marketing site underneath must be revealed rather than left hidden.
 *
 * Failing to clear it is a blank-page bug, not a flash bug: the redirect to
 * `/a/arche_make_a_request` is client-side routing in the SAME document, so
 * the attribute — and the `visibility:hidden` on `#root` it drives — would
 * otherwise survive for the whole session.
 */

/** Must match `SESSION_HINT_ATTRIBUTE` in `@fias/site-snapshot`. */
const BOOT_SESSION_HINT_ATTRIBUTE = 'data-fias-session-hint';

/**
 * Whether the snapshot's inline script decided this visitor is probably
 * signed in. False everywhere the shell is served directly (dev, and any
 * route without a snapshot) — those have no inline script to set it.
 */
export function hasBootSessionHint(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute(BOOT_SESSION_HINT_ATTRIBUTE) === '1';
}

/**
 * Drops the pre-JS hint, revealing `#root` and hiding the splash.
 *
 * Idempotent and safe to call when no snapshot was served — `removeAttribute`
 * on an absent attribute is a no-op.
 */
export function clearBootSessionHint(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.removeAttribute(BOOT_SESSION_HINT_ATTRIBUTE);
}
